import React, { useEffect, useMemo, useRef, useState } from "react";
import { Pressable, StyleSheet, View } from "react-native";
import { UserPlusIcon } from "@phosphor-icons/react";
import { EditingTeamRole } from "@axio/graphql/generated/graphql";

import CustomButton from "../../../../../components/common/general/CustomButton/CustomButton";
import CustomDropdown from "../../../../../components/common/general/CustomDropDown/CustomDropdown";
import CustomSearchInput from "../../../../../components/common/general/CustomSearchInput/CustomSearchInput";
import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import { colors } from "../../../../../styles/design-system";
import type { CourseModernizerReviewTeamManagement } from "../types/reviewTypes";

export type TeamManagementUserOption = {
  id: string;
  label: string;
  subtitle?: string | null;
};

type EditingTeamInput = Array<{ userId: string; role: EditingTeamRole }>;

type TeamManagementPanelProps = {
  team: CourseModernizerReviewTeamManagement;
  currentUserId: string | null;
  userOptions: TeamManagementUserOption[];
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  selectedRole: EditingTeamRole;
  onSelectedRoleChange: (role: EditingTeamRole) => void;
  isSaving?: boolean;
  onEditingTeamChange: (nextMembers: EditingTeamInput) => void;
};

const ROLE_OPTIONS = [
  { label: "Instructor", value: EditingTeamRole.Instructor },
  { label: "Instructional Designer", value: EditingTeamRole.InstructionalDesigner },
  { label: "Observer", value: EditingTeamRole.Observer },
];

const ROLE_LABELS: Record<EditingTeamRole, string> = {
  [EditingTeamRole.Architect]: "Architect",
  [EditingTeamRole.Instructor]: "Instructor",
  [EditingTeamRole.InstructionalDesigner]: "Instructional Designer",
  [EditingTeamRole.Observer]: "Observer",
};

function getInitials(label: string): string {
  const initials = label
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
  return initials || "U";
}

function toEditingTeamInput(team: CourseModernizerReviewTeamManagement): EditingTeamInput {
  return team.editingTeam.map((member) => ({ userId: member.user.id, role: member.role }));
}

function TeamManagementPanel({
  team,
  currentUserId,
  userOptions,
  searchQuery,
  onSearchQueryChange,
  selectedRole,
  onSelectedRoleChange,
  isSaving = false,
  onEditingTeamChange,
}: TeamManagementPanelProps) {
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const editorIds = useMemo(() => new Set(team.editingTeam.map((member) => member.user.id)), [team.editingTeam]);
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const visibleUserOptions = useMemo(() => {
    if (!normalizedSearchQuery) {
      return [];
    }

    return userOptions.filter((option) => {
      if (option.id === team.architect?.id || editorIds.has(option.id)) {
        return false;
      }

      return [option.label, option.subtitle ?? ""].some((value) => value.toLowerCase().includes(normalizedSearchQuery));
    });
  }, [editorIds, normalizedSearchQuery, team.architect?.id, userOptions]);
  const selectedUser = userOptions.find((option) => option.id === selectedUserId) ?? null;
  const isEmpty = team.editingTeam.length === 0;

  const updateMemberRole = (userId: string, role: EditingTeamRole) => {
    onEditingTeamChange(
      toEditingTeamInput(team).map((member) => (member.userId === userId ? { ...member, role } : member))
    );
  };

  const removeMember = (userId: string) => {
    onEditingTeamChange(toEditingTeamInput(team).filter((member) => member.userId !== userId));
  };

  const addMember = () => {
    if (!selectedUser || selectedUser.id === team.architect?.id || editorIds.has(selectedUser.id)) {
      return;
    }

    onEditingTeamChange([...toEditingTeamInput(team), { userId: selectedUser.id, role: selectedRole }]);
    setSelectedUserId(null);
    onSearchQueryChange("");
  };

  return (
    <View>
      {!isEmpty && (
        <View style={styles.inviteBlock}>
          <CustomText
            text="Everyone with access to this course. As the architect, you're the only one who can invite or remove people."
            size="s"
            style={styles.inviteHelpText}
            useTranslationText={false}
          />
          <AddMemberControl
            searchQuery={searchQuery}
            onSearchQueryChange={onSearchQueryChange}
            userOptions={visibleUserOptions}
            selectedUser={selectedUser}
            selectedRole={selectedRole}
            isSaving={isSaving}
            onSelectUser={setSelectedUserId}
            onSelectedRoleChange={onSelectedRoleChange}
            onAddMember={addMember}
          />
        </View>
      )}

      <View style={styles.rosterCard}>
        <GroupHeader label="Architect" />
        {team.architect ? (
          <MemberIdentityRow
            name={team.architect.name}
            currentUserId={currentUserId}
            userId={team.architect.id}
            roleLabel="Architect"
            trailing={
              <CustomText text="Admin-assigned" size="xs" style={styles.mutedText} useTranslationText={false} />
            }
          />
        ) : (
          <CustomText
            text="No architect assigned"
            size="s"
            style={[styles.secondaryText, styles.emptyRosterLine]}
            useTranslationText={false}
          />
        )}

        {isEmpty ? (
          <View style={styles.emptyState}>
            <View style={styles.emptyIconCircle}>
              <UserPlusIcon size={20} color={colors.brand[500]} weight="bold" />
            </View>
            <CustomText
              text="Invite your first team member"
              size="s"
              weight="bold"
              style={styles.emptyTitle}
              useTranslationText={false}
            />
            <CustomText
              text="Bring in faculty or co-designers to review this course alongside you and hand off work to as you go. As the architect, you're the only one who can manage access."
              size="s"
              style={styles.emptyDescription}
              useTranslationText={false}
            />
            <View style={styles.emptyInviteControl}>
              <AddMemberControl
                searchQuery={searchQuery}
                onSearchQueryChange={onSearchQueryChange}
                userOptions={visibleUserOptions}
                selectedUser={selectedUser}
                selectedRole={selectedRole}
                isSaving={isSaving}
                onSelectUser={setSelectedUserId}
                onSelectedRoleChange={onSelectedRoleChange}
                onAddMember={addMember}
              />
            </View>
          </View>
        ) : (
          <>
            <GroupHeader label={`Editors · ${team.editingTeam.length}`} />
            {team.editingTeam.map((member, index) => (
              <View key={member.user.id} style={[styles.editorRow, index > 0 && styles.rowDivider]}>
                <MemberIdentity
                  name={member.user.name}
                  currentUserId={currentUserId}
                  userId={member.user.id}
                  roleLabel={ROLE_LABELS[member.role]}
                />
                <View style={styles.editorActions}>
                  <CustomDropdown
                    options={ROLE_OPTIONS}
                    value={member.role}
                    onChange={(role) => updateMemberRole(member.user.id, role as EditingTeamRole)}
                    accessibilityLabel={`Role for ${member.user.name}`}
                    containerStyle={styles.roleDropdown}
                    dropdownStyle={styles.roleDropdownButton}
                    fullWidth={false}
                    disabled={isSaving}
                  />
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel={`Remove ${member.user.name}`}
                    disabled={isSaving}
                    onPress={() => removeMember(member.user.id)}
                    style={({ hovered, pressed }) => [
                      styles.removeButton,
                      (hovered || pressed) && styles.removeButtonActive,
                      isSaving && styles.disabledAction,
                    ]}>
                    <CustomText
                      text="Remove"
                      size="xs"
                      weight="medium"
                      style={styles.removeText}
                      useTranslationText={false}
                    />
                  </Pressable>
                </View>
              </View>
            ))}
          </>
        )}
      </View>
    </View>
  );
}

type AddMemberControlProps = {
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  userOptions: TeamManagementUserOption[];
  selectedUser: TeamManagementUserOption | null;
  selectedRole: EditingTeamRole;
  isSaving: boolean;
  onSelectUser: (userId: string) => void;
  onSelectedRoleChange: (role: EditingTeamRole) => void;
  onAddMember: () => void;
};

function AddMemberControl({
  searchQuery,
  onSearchQueryChange,
  userOptions,
  selectedUser,
  selectedRole,
  isSaving,
  onSelectUser,
  onSelectedRoleChange,
  onAddMember,
}: AddMemberControlProps) {
  const searchControlRef = useRef<React.ElementRef<typeof View> | null>(null);
  const [isSearchOpen, setIsSearchOpen] = useState(() => searchQuery.trim().length > 0);
  const hasSearchResults = isSearchOpen && searchQuery.trim().length > 0;

  useEffect(() => {
    if (!isSearchOpen || typeof document === "undefined") {
      return undefined;
    }

    const handleOutsidePress = (event: MouseEvent | TouchEvent) => {
      const searchNode = searchControlRef.current as unknown as { contains?: (node: Node) => boolean } | null;
      if (!searchNode?.contains || !(event.target instanceof Node)) {
        setIsSearchOpen(false);
        return;
      }

      if (!searchNode.contains(event.target)) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsidePress, true);
    document.addEventListener("touchstart", handleOutsidePress, true);

    return () => {
      document.removeEventListener("mousedown", handleOutsidePress, true);
      document.removeEventListener("touchstart", handleOutsidePress, true);
    };
  }, [isSearchOpen]);

  const handleSearchQueryChange = (value: string) => {
    setIsSearchOpen(true);
    onSearchQueryChange(value);
  };

  const handleSelectUser = (userId: string) => {
    onSelectUser(userId);
    setIsSearchOpen(false);
  };

  return (
    <View ref={searchControlRef} style={styles.addMemberControl}>
      <CustomText
        text="Invite by name or email"
        size="xs"
        weight="medium"
        style={styles.fieldLabel}
        useTranslationText={false}
      />
      <CustomSearchInput
        placeholder="Search people in your university"
        searchQuery={searchQuery}
        setSearchQuery={handleSearchQueryChange}
        minWidth="100%"
        containerStyle={styles.searchControlInput}
        onFocus={() => setIsSearchOpen(true)}
      />

      {hasSearchResults && (
        <View style={styles.searchResults}>
          {userOptions.length > 0 ? (
            userOptions.map((option) => (
              <Pressable
                key={option.id}
                accessibilityRole="button"
                accessibilityLabel={`Select team member ${option.label}`}
                onPress={() => handleSelectUser(option.id)}
                style={[styles.searchResultRow, selectedUser?.id === option.id && styles.searchResultRowSelected]}>
                <View style={styles.searchAvatar}>
                  <CustomText
                    text={getInitials(option.label)}
                    size="xs"
                    weight="bold"
                    style={styles.avatarText}
                    useTranslationText={false}
                  />
                </View>
                <View style={styles.searchResultText}>
                  <CustomText text={option.label} size="s" weight="medium" useTranslationText={false} />
                  {option.subtitle ? (
                    <CustomText text={option.subtitle} size="xs" style={styles.mutedText} useTranslationText={false} />
                  ) : null}
                </View>
              </Pressable>
            ))
          ) : (
            <CustomText text="No matching users" size="s" style={styles.mutedText} useTranslationText={false} />
          )}
        </View>
      )}

      {selectedUser ? (
        <View style={styles.addFooter}>
          <CustomDropdown
            options={ROLE_OPTIONS}
            value={selectedRole}
            onChange={(role) => onSelectedRoleChange(role as EditingTeamRole)}
            accessibilityLabel="New member role"
            containerStyle={styles.addRoleDropdown}
            dropdownStyle={styles.addRoleDropdownButton}
            fullWidth={false}
            disabled={isSaving}
          />
          <CustomButton
            text="Add"
            useTranslationText={false}
            size="s"
            styleType="primary"
            disabled={isSaving}
            onPress={onAddMember}
            style={styles.addButton}
          />
        </View>
      ) : null}
    </View>
  );
}

function GroupHeader({ label }: { label: string }) {
  return (
    <View style={styles.groupHeader}>
      <CustomText text={label} size="xs" weight="bold" style={styles.groupHeaderText} useTranslationText={false} />
    </View>
  );
}

function MemberIdentityRow({
  name,
  currentUserId,
  userId,
  roleLabel,
  trailing,
}: {
  name: string;
  currentUserId: string | null;
  userId: string;
  roleLabel: string;
  trailing?: React.ReactNode;
}) {
  return (
    <View style={styles.memberRow}>
      <MemberIdentity name={name} currentUserId={currentUserId} userId={userId} roleLabel={roleLabel} />
      {trailing}
    </View>
  );
}

function MemberIdentity({
  name,
  currentUserId,
  userId,
  roleLabel,
}: {
  name: string;
  currentUserId: string | null;
  userId: string;
  roleLabel: string;
}) {
  return (
    <View style={styles.memberIdentity}>
      <View style={styles.avatar}>
        <CustomText
          text={getInitials(name)}
          size="xs"
          weight="bold"
          style={styles.avatarText}
          useTranslationText={false}
        />
      </View>
      <View style={styles.memberText}>
        <CustomText
          text={currentUserId === userId ? `${name} (you)` : name}
          size="s"
          weight="bold"
          style={styles.memberName}
          useTranslationText={false}
          numberOfLines={1}
        />
        <CustomText
          text={roleLabel}
          size="xs"
          style={styles.secondaryText}
          useTranslationText={false}
          numberOfLines={1}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  inviteBlock: {
    marginBottom: 20,
    gap: 12,
  },
  inviteHelpText: {
    color: colors.grey[600],
    fontSize: 14,
    lineHeight: 20,
  },
  rosterCard: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 16,
    overflow: "hidden",
  },
  groupHeader: {
    paddingHorizontal: 20,
    paddingVertical: 8,
    backgroundColor: colors.grey[50],
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[100],
  },
  groupHeaderText: {
    color: colors.grey[500],
    textTransform: "uppercase",
    letterSpacing: 0,
    fontSize: 11,
    lineHeight: 16.5,
  },
  memberRow: {
    minHeight: 64,
    paddingHorizontal: 20,
    paddingVertical: 12,
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  editorRow: {
    minHeight: 68,
    paddingHorizontal: 20,
    paddingVertical: 12,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
  },
  rowDivider: {
    borderTopWidth: 1,
    borderTopColor: colors.grey[100],
  },
  memberIdentity: {
    flex: 1,
    minWidth: 0,
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.brand[100],
  },
  searchAvatar: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.grey[100],
  },
  avatarText: {
    color: colors.brand[700],
  },
  memberText: {
    flex: 1,
    minWidth: 0,
  },
  memberName: {
    color: colors.grey[900],
    fontSize: 14,
    lineHeight: 21,
  },
  secondaryText: {
    color: colors.grey[700],
    fontSize: 12,
    lineHeight: 18,
  },
  mutedText: {
    color: colors.grey[500],
    fontSize: 12,
    lineHeight: 18,
  },
  fieldLabel: {
    color: colors.grey[700],
    fontSize: 12,
    lineHeight: 18,
  },
  editorActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
  },
  roleDropdown: {
    width: 190,
    zIndex: 1000,
  },
  roleDropdownButton: {
    minHeight: 36,
    borderRadius: 8,
  },
  removeButton: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
  },
  removeButtonActive: {
    backgroundColor: "#FEF3F2",
  },
  removeText: {
    color: colors.error[600],
  },
  disabledAction: {
    opacity: 0.5,
  },
  emptyRosterLine: {
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  emptyState: {
    paddingHorizontal: 24,
    paddingVertical: 48,
    alignItems: "center",
  },
  emptyIconCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.brand[50],
    marginBottom: 16,
  },
  emptyTitle: {
    color: colors.grey[900],
    fontSize: 14,
    lineHeight: 21,
  },
  emptyDescription: {
    color: colors.grey[600],
    fontSize: 14,
    lineHeight: 20,
    marginTop: 6,
    maxWidth: 420,
    textAlign: "center",
  },
  emptyInviteControl: {
    width: "100%",
    maxWidth: 384,
    marginTop: 20,
  },
  addMemberControl: {
    gap: 6,
    maxWidth: 512,
    width: "100%",
  },
  searchControlInput: {
    flex: 0,
    minHeight: 46,
    width: "100%",
  },
  searchResults: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 16,
    overflow: "hidden",
  },
  searchResultRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    paddingHorizontal: 12,
    paddingVertical: 9,
  },
  searchResultRowSelected: {
    backgroundColor: colors.grey[50],
  },
  searchResultText: {
    flex: 1,
    minWidth: 0,
  },
  addFooter: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 2,
  },
  addRoleDropdown: {
    width: 210,
    zIndex: 900,
  },
  addRoleDropdownButton: {
    minHeight: 38,
    borderRadius: 8,
  },
  addButton: {
    minHeight: 38,
    paddingHorizontal: 14,
  },
});

export default TeamManagementPanel;
